import EMPLOYER_GUARD from '@/core/guards/employer.guard';
import PAYMENT_SYSTEM_GUARD from '@/core/guards/payment-system.guard';
import VALID_ON_FILTER_GUARD from '@/core/guards/valid-on-filter.guard';
import { AddonKeys } from '@/core/models/addons.interface';
import store from '@/core/store';
import i18n from '@/i18n';
import { PaymentSystemKey } from '@nxtpeople/nxt-styles';
import { RouteRecordRaw } from 'vue-router';
import ContractAndPerformanceDataTemplates from './components/customer-details/contract-and-performance-data/contract-templates/ContractAndPerformanceDataTemplates.vue';
import ContractAndPerformanceDataGeneral from './components/customer-details/contract-and-performance-data/general/ContractAndPerformanceDataGeneral.vue';
import ContractAndPerformanceDataPropertyTypes from './components/customer-details/contract-and-performance-data/property-types/ContractAndPerformanceDataPropertyTypes.vue';
import ContractAndPerformanceDataWorkCycles from './components/customer-details/contract-and-performance-data/work-cycles/ContractAndPerformanceDataWorkCycles.vue';
import ContractAndPerformanceDataTaxDiscounts from './components/customer-details/contract-data/ContractAndPerformanceDataTaxDiscounts.vue';

const customerRoutes: Array<RouteRecordRaw> = [
   {
      path: '',
      name: 'CustomersOverview',
      component: () => import('@/modules/customer/pages/CustomersOverview.vue'),
   },
   {
      path: 'create',
      name: 'CreateCustomer',
      redirect: '/customers#create-customer',
      meta: { searchable: true, titleKey: 'actions.add', aliasKeys: ['customers.customer'] },
   },
   {
      path: ':id',
      name: 'CustomerDetails',
      component: () => import('@/modules/customer/pages/CustomerDetails.vue'),
      meta: { skipTitleGuard: true },

      children: [
         {
            alias: '',
            path: 'location-and-functionsdata',
            name: 'CustomerLocationAndFunctionsData',
            component: () =>
               import('@/modules/customer/components/customer-details/location-and-function-data/LocationAndFunctionData.vue'),
         },
         {
            path: 'contract-data',
            name: 'CustomerContractData',
            component: () => import('@/modules/customer/components/customer-details/contract-data/CustomerContractData.vue'),
            children: [
               {
                  path: '',
                  name: 'ContractAndPerformanceDataGeneral',
                  component: ContractAndPerformanceDataGeneral,
               },
               {
                  path: 'work-cycles',
                  name: 'ContractAndPerformanceDataWorkCycles',
                  component: ContractAndPerformanceDataWorkCycles,
               },
               {
                  path: 'templates',
                  name: 'ContractAndPerformanceDataTemplates',
                  component: ContractAndPerformanceDataTemplates,
               },
               {
                  path: 'property-types',
                  name: 'ContractAndPerformanceDataPropertyTypes',
                  component: ContractAndPerformanceDataPropertyTypes,
               },
               {
                  path: 'tax-discounts',
                  name: 'ContractAndPerformanceDataTaxDiscounts',
                  component: ContractAndPerformanceDataTaxDiscounts,
               },
            ],
         },
         {
            path: 'performance-data',
            name: 'CustomerPerformanceData',
            component: () => import('@/modules/customer/components/customer-details/performance-data/CustomerPerformanceData.vue'),
            children: [
               {
                  path: '',
                  name: 'CustomerPerformanceDataGeneral',
                  component: () =>
                     import('@/modules/customer/components/customer-details/performance-data/CustomerPerformanceDataGeneral.vue'),
               },
               {
                  path: 'mail-performance',
                  name: 'CustomerPerformanceDataMailPerformance',
                  component: () =>
                     import('@/modules/customer/components/customer-details/performance-data/CustomerPerformanceDataMailPerformance.vue'),
                  beforeEnter: [EMPLOYER_GUARD],
               },
               {
                  path: 'payment-system',
                  name: 'CustomerPerformanceDataPaymentSystem',
                  component: () =>
                     import('@/modules/customer/components/customer-details/performance-data/CustomerPerformanceDataPaymentSystem.vue'),
                  beforeEnter: [PAYMENT_SYSTEM_GUARD(PaymentSystemKey.ETM)],
               },
               {
                  path: 'benefit-settings',
                  name: 'CustomerPerformanceDataBenefitSettings',
                  component: () =>
                     import('@/modules/customer/components/customer-details/performance-data/benefits-settings/BenefitSettings.vue'),
                  beforeEnter: [
                     (to, from, next) => {
                        const addons = store.state.userInfo?.userInfo?.addons || [];
                        if (!addons.includes(AddonKeys.Benefits)) {
                           return next(false);
                        }
                        return next();
                     },
                  ],
               },
            ],
         },
         {
            path: 'credit-settings',
            name: 'CustomerCreditSettings',
            component: () => import('@/modules/customer/components/customer-details/credit-settings/CustomerCreditSettings.vue'),
            children: [
               {
                  path: '',
                  name: 'CustomerCreditSettingsCreditAndInvoiceData',
                  component: () =>
                     import('@/modules/customer/components/customer-details/credit-settings/CustomerCreditSettingsCreditAndInvoiceData.vue'),
               },
               {
                  path: 'invoice-groups',
                  name: 'CustomerCreditSettingsInvoiceGroups',
                  component: () =>
                     import('@/modules/customer/components/customer-details/credit-settings/CustomerCreditSettingsInvoiceGroups.vue'),
               },
               {
                  path: 'customer-list-exports',
                  name: 'CustomerCreditSettingsCustomerListExports',
                  component: () =>
                     import('@/modules/customer/components/customer-details/credit-settings/CustomerCreditSettingsCustomerListExports.vue'),
               },
            ],
         },
         {
            path: 'contacts',
            name: 'CustomerContacts',
            component: () => import('@/modules/customer/components/customer-details/contacts/Contacts.vue'),
         },
         {
            path: 'cooperation-agreements',
            name: 'CustomerCooperationAgreements',
            component: () => import('@/modules/customer/components/customer-details/cooperation-agreements/CooperationAgreements.vue'),
            beforeEnter: [
               EMPLOYER_GUARD,
               (to, from, next) => {
                  const addons = store.state.userInfo?.userInfo?.addons || [];
                  if (!addons.includes(AddonKeys.LetsworkSwok)) {
                     return next({
                        path: '/error',
                        query: {
                           error_code: '403',
                           error_title: i18n.global.t('generic.access-denied'),
                           error_message: i18n.global.t('generic.u-have-no-access'),
                           error_action: 'home-or-logout',
                        },
                     });
                  }
                  return next();
               },
            ],
         },
         {
            path: 'commercial-agreements',
            name: 'CustomerCommercialAgreements',
            component: () =>
               import('@/modules/customer/components/customer-details/commercial-agreements/CustomerCommercialAgreements.vue'),
            beforeEnter: [
               EMPLOYER_GUARD,
               VALID_ON_FILTER_GUARD,
               (to, from, next) => {
                  if (to.query.hide_customer_group_co !== undefined || to.query.hide_general_co !== undefined) return next();
                  return next({ ...to, query: { ...to.query, hide_customer_group_co: 1, hide_general_co: 1 } });
               },
            ],
         },
         {
            path: 'performance-triggers',
            name: 'CompanyPerformanceTriggers',
            component: () => import('@/modules/customer/components/customer-details/performance-triggers/PerformanceTriggers.vue'),
            beforeEnter: EMPLOYER_GUARD,
         },
         {
            path: 'commuter-traffic-fees',
            name: 'CompanyCommuterTrafficFees',
            component: () => import('@/modules/customer/components/customer-details/commuter-traffic-fees/CompanyCommuterTrafficFees.vue'),
         },
         {
            path: 'documents',
            name: 'CustomerDocuments',
            component: () => import('@/modules/customer/components/customer-details/documents/CustomerDocuments.vue'),
         },
      ],
   },
];

export default customerRoutes;
